import {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
  putRequest
} from "@/views/academy/api/apiRequests";
import { TeamRegistrationApiEndpoints } from "@/views/academy/teamregistration/TeamRegistrationApiEndpoints";
import { i18n } from "@/plugins/i18n";
import {
  ApiRequestHeaderTypes,
  ApiRequestHeaderValueTypes
} from "@/views/academy/enums/ApiRequestHeaderTypes";
import { AcademyNavigatorApiEndpoints } from "@/views/academy/common/academynavigator/AcademyNavigatorApiEndpoints";
import { prepareQueryParams } from "@/helpers/prepareQueryParams";
import router from "@/router";
import axios from "axios";
import { bus } from "@/main";

const state = () => ({
  registrationTeams: null,
  isAbsorbFees: false,
  registrationName: "",
  isAcceptPayments: true,
  mainPaymentAccount: "",
  registrationDetails: {}
});

const getters = {
  registrationTeams: (state: { registrationTeams: [] }) =>
    state.registrationTeams,

  isAbsorbFees: (state: { isAbsorbFees: boolean }) => state.isAbsorbFees,

  registrationName: (state: { registrationName: string }) =>
    state.registrationName,

  registrationDetails: (state: { registrationDetails: object }) =>
    state.registrationDetails,

  isAcceptPayments: (state: { isAcceptPayments: boolean }) =>
    state.isAcceptPayments,

  mainPaymentAccount: (state: { mainPaymentAccount: string }) =>
    state.mainPaymentAccount
};

const mutations = {
  setRegistrationTeamsFromSetup: (
    state: { registrationTeams: any },
    payload: { teams: [] }
  ) => {
    state.registrationTeams = payload.teams;
  },

  setAbsorbFeesValue: (
    state: { isAbsorbFees: boolean },
    payload: { isAbsorbFees: boolean }
  ) => {
    state.isAbsorbFees = payload.isAbsorbFees;
  },

  setRegistrationName: (
    state: { registrationName: string },
    payload: { registrationName: string }
  ) => {
    state.registrationName = payload.registrationName;
  },

  setRegistrationDetails: (
    state: { registrationDetails: object },
    payload: { registrationDetails: object }
  ) => {
    state.registrationDetails = payload.registrationDetails;
  },

  setAcceptPayments: (
    state: { isAcceptPayments: boolean },
    payload: { isAcceptPayments: boolean }
  ) => {
    state.isAcceptPayments = payload.isAcceptPayments;
  },

  setMainPaymentAccount: (
    state: { mainPaymentAccount: string },
    payload: { mainPaymentAccount: string }
  ) => {
    state.mainPaymentAccount = payload.mainPaymentAccount;
  }
};

const actions = {
  getOrganizationTeams: async (
    context: any,
    payload: { organizationId: string; callback: Function }
  ) => {
    const { organizationId, callback } = payload;
    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_ORGANIZATION_TEAMS}/${organizationId}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  calculateAddTeamPrices: async (
    context: any,
    payload: {
      absorbFees: boolean;
      registrationOptionId: string;
      priceInDollars: number;
      callback: Function;
      failureCallback: Function;
    }
  ) => {
    const {
      absorbFees,
      registrationOptionId,
      priceInDollars,
      callback,
      failureCallback
    } = payload;

    const body = {
      registrationId: router.currentRoute.params.id,
      absorbFees,
      registrationOptionId,
      priceInCents: priceInDollars * 100
    };
    const res: any = await axios
      .post(
        `/rest-api/${TeamRegistrationApiEndpoints.CALCULATE_ADD_TEAM_PRICES}`,
        body
      )
      .then(res => res?.data)
      .catch(err => err.response?.data);
    if (res?.status === "success") {
      callback && callback(res);
    } else if (
      res?.status === "error" &&
      res?.code == "REGISTRATION_PRICE_LOWER_THAN_REGISTRATION_FEE"
    ) {
      failureCallback && failureCallback(res);
    } else {
      bus.$emit("errorSnack", res.message);
    }
  },

  setupRegistration: async (
    context: any,
    payload: {
      id: string;
      body: {};
      callback: Function;
    }
  ) => {
    const { id, body, callback } = payload;
    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.SETUP_REGISTRATION}/${id}`,
        body
      );
      if (res.status === "success") {
        callback && callback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  autoSaveSetupRegistration: async (
    context: any,
    payload: {
      id: string;
      body: {};
      callback: Function;
    }
  ) => {
    const { id, body, callback } = payload;

    try {
      const res: any = await patchRequest(
        `${TeamRegistrationApiEndpoints.AUTO_SAVE_SETUP_REGISTRATION}/${id}`,
        body
      );
      if (res.status === "success") {
        callback && callback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchSetupFormStepDetails: async (
    context: any,
    payload: { id: string; callback: Function }
  ) => {
    const { id, callback } = payload;
    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_SETUP_FORM_STEP_DETAILS}/${id}`
      );
      if (res.status === "success") {
        console.log(res.data);
        context.commit("setRegistrationDetails", {
          registrationDetails: res.data
        });
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  createTag: async (
    context: any,
    payload: { tag: string; registrationSettingId: string; callback: Function }
  ) => {
    const { tag, registrationSettingId } = payload;

    const body = {
      tag
    };
    try {
      let tags = null;
      const res: any = await putRequest(
        TeamRegistrationApiEndpoints.CREATE_TAG,
        body
      );
      if (res.status === "success") {
        await context.dispatch("bindTag", {
          id: registrationSettingId,
          registrationSettingTagId: res.data.id
        });
        await context.dispatch("getTagsByRegistrationSettingId", {
          registrationSettingId,
          callback: (res: { data: { tags: any } }) => {
            tags = res;
          }
        });
        await context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.added_successfully") },
          {
            root: true
          }
        );

        payload.callback && payload.callback(tags);
      }
    } catch (e) {
      console.log(e);
    }
  },

  getTagsByRegistrationSettingId: async (
    context: any,
    payload: { registrationSettingId: string; callback: Function }
  ) => {
    const { registrationSettingId } = payload;
    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_TAGS_BY_REGISTRATION_SETTING_ID}/${registrationSettingId}`
      );
      if (res.status === "success") {
        payload?.callback && payload.callback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  searchTags: async (
    context: any,
    payload: { filter: any; callback: (arg0: any) => any }
  ) => {
    try {
      const query = prepareQueryParams({ filter: payload?.filter });
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.SEARCH_TAGS}${query}`
      );
      if (res.status === "success") {
        payload?.callback && payload.callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  bindTag: async (
    context: any,
    payload: { id: string; registrationSettingTagId: string }
  ) => {
    const { id, registrationSettingTagId } = payload;
    try {
      await putRequest(
        `${TeamRegistrationApiEndpoints.BIND_TAG}/${id}/tag/${registrationSettingTagId}`
      );
    } catch (e) {
      console.log(e);
    }
  },

  unbindTag: async (
    context: any,
    payload: {
      id: string;
      registrationSettingTagId: string;
      callback: Function;
    }
  ) => {
    const { id, registrationSettingTagId, callback } = payload;
    try {
      let tags = null;
      const res: any = await deleteRequest(
        `${TeamRegistrationApiEndpoints.BIND_TAG}/${id}/tag/${registrationSettingTagId}`
      );
      if (res.status === "success") {
        await context.dispatch("getTagsByRegistrationSettingId", {
          registrationSettingId: id,
          callback: (res: { data: { tags: any } }) => {
            tags = res.data.tags;
          }
        });
        await context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.removed_successfully") },
          {
            root: true
          }
        );
        callback && callback(tags);
      }
    } catch (e) {
      console.log(e);
    }
  },

  deleteTags: async (
    context: any,
    payload: { registrationSettingTagId: string }
  ) => {
    const { registrationSettingTagId } = payload;
    try {
      const res: any = await deleteRequest(
        `${TeamRegistrationApiEndpoints.DELETE_TAG}/${registrationSettingTagId}`
      );
      if (res.status === "success") {
        await context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.removed_successfully") },
          {
            root: true
          }
        );
      }
    } catch (e) {
      console.log(e);
    }
  },

  calculateInstallments: async (
    context: any,
    payload: {
      installmentsRequest: [];
      priceInCents: null;
      registrationOptionId: string;
      absorbFees: number;
      callback: Function;
    }
  ) => {
    const {
      installmentsRequest,
      priceInCents,
      registrationOptionId,
      absorbFees,
      callback
    } = payload;
    const body = {
      registrationId: router.currentRoute.params.id,
      installmentsRequest,
      priceInCents,
      registrationOptionId,
      absorbFees,
      callback
    };
    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.CALCULATE_INSTALLMENTS}`,
        body
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchPromoCodes: async (
    context: any,
    payload: {
      registrationId: string;
      callback: Function;
    }
  ) => {
    const { registrationId, callback } = payload;
    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_PROMO_CODES}/${registrationId}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  addNewPromoCode: async (
    context: any,
    payload: {
      registrationId: string;
      codeTitle: string;
      promoCodeType: string;
      discountInCents: number;
      discountInPercentile: string;
      expires: string;
      maxUse: any;
      callback: Function;
    }
  ) => {
    const {
      registrationId,
      codeTitle,
      promoCodeType,
      discountInCents,
      discountInPercentile,
      expires,
      maxUse,
      callback
    } = payload;
    const body = {
      codeTitle,
      promoCodeType,
      discountInCents,
      discountInPercentile,
      expires,
      maxUse: +maxUse || null
    };
    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.ADD_NEW_PROMO_CODE}/${registrationId}`,
        body
      );

      if (res.status === "success") {
        await context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.added_successfully") },
          {
            root: true
          }
        );
        callback && callback();
      }
    } catch (e) {
      console.log(e);
    }
  },

  editPromoCode: async (
    context: any,
    payload: {
      promoCodeId: string;
      codeTitle: string;
      promoCodeType: string;
      discountInCents: string;
      discountInPercentile: string;
      expires: string;
      maxUse: any;
      callback: Function;
    }
  ) => {
    const {
      promoCodeId,
      codeTitle,
      promoCodeType,
      discountInCents,
      discountInPercentile,
      expires,
      maxUse,
      callback
    } = payload;
    const body = {
      codeTitle,
      promoCodeType,
      discountInCents,
      discountInPercentile,
      expires,
      maxUse: +maxUse || null
    };
    try {
      const res: any = await putRequest(
        `${TeamRegistrationApiEndpoints.UPDATE_PROMO_CODE}/${promoCodeId}`,
        body
      );
      if (res.status === "success") {
        callback && callback();
      }
    } catch (e) {
      console.log(e);
    }
  },

  deletePromoCode: async (
    context: any,
    payload: {
      promoCodeId: string;
      callback: Function;
    }
  ) => {
    const { promoCodeId, callback } = payload;

    try {
      await deleteRequest(
        `${TeamRegistrationApiEndpoints.DELETE_PROMO_CODE}/${promoCodeId}`
      );
      await context.dispatch(
        "snackbar/SHOW_SNACKBAR",
        { text: i18n.tc("snackbar_message.removed_successfully") },
        {
          root: true
        }
      );
      callback && callback(promoCodeId);
    } catch (e) {
      console.log(e);
    }
  },

  fetchInstallments: async (
    context: any,
    payload: {
      registrationId: string;
      callback: Function;
    }
  ) => {
    const { registrationId, callback } = payload;

    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_INSTALLMENTS}/${registrationId}`
      );
      if (res.status === "success" && res.data != null) {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  saveInstallments: async (
    context: any,
    payload: {
      registrationId: string;
      items: [];
      paymentPlanEnabled: boolean;
      callback: Function;
    }
  ) => {
    const { registrationId, items, paymentPlanEnabled, callback } = payload;

    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.SAVE_INSTALLMENTS}/${registrationId}`,
        { items, paymentPlanEnabled }
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchAddOns: async (
    context: any,
    payload: {
      registrationId: string;
      callback: Function;
    }
  ) => {
    const { registrationId, callback } = payload;

    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_ADD_ONS}/${registrationId}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  createAddOn: async (
    context: any,
    payload: {
      registrationId: string;
      addOnPayload: any;
      callback: Function;
    }
  ) => {
    const {
      registrationId,
      addOnPayload: {
        name,
        price,
        description,
        minQuantity,
        maxQuantity,
        options,
        fileMetadataId
      },
      callback
    } = payload;

    const body = {
      addOnName: name,
      addOns: options.map((item: { isEditMode: any }) => {
        delete item.isEditMode;
        return item;
      }),
      customerCanNotPurchaseMoreThan: +maxQuantity || null,
      customerMustPurchaseAtLeast: +minQuantity,
      description,
      fileMetadataId,
      priceInCents: price,
      callback
    };
    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.CREATE_ADD_ON}/${registrationId}`,
        body
      );
      if (res.status === "success") {
        await context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.added_successfully") },
          {
            root: true
          }
        );
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  updateAddOn: async (
    context: any,
    payload: {
      addOnId: string;
      addOnPayload: any;
      callback: Function;
    }
  ) => {
    const {
      addOnId,
      addOnPayload: {
        name,
        price,
        description,
        minQuantity,
        maxQuantity,
        options,
        fileMetadataId
      },
      callback
    } = payload;

    const body = {
      addOnName: name,
      addOns: options.map((item: { isEditMode: any }) => {
        delete item.isEditMode;
        return item;
      }),
      customerCanNotPurchaseMoreThan: +maxQuantity || null,
      customerMustPurchaseAtLeast: +minQuantity,
      description,
      fileMetadataId,
      priceInCents: price,
      callback
    };
    try {
      const res: any = await putRequest(
        `${TeamRegistrationApiEndpoints.UPDATE_ADD_ON}/${addOnId}`,
        body
      );
      if (res.status === "success") {
        await context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.updated_successfully") },
          {
            root: true
          }
        );
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  deleteAddOn: async (
    context: any,
    payload: {
      id: any;
      callback: Function;
    }
  ) => {
    const { id, callback } = payload;

    try {
      const res: any = await deleteRequest(
        `${TeamRegistrationApiEndpoints.DELETE_ADD_ON}/${id}`
      );
      if (res.status === "success") {
        await context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.removed_successfully") },
          {
            root: true
          }
        );
        callback && callback();
      }
    } catch (e) {
      console.log(e);
    }
  },

  uploadFile: async (
    context: any,
    payload: { file: any; callback: Function }
  ) => {
    const { file, callback } = payload;
    try {
      const config = {
        headers: {
          [ApiRequestHeaderTypes.CONTENT_TYPE]:
            ApiRequestHeaderValueTypes.MULTIPART_FORM_DATA
        },
        timeout: 0
      };

      const formData = new FormData();
      formData.append("file", file);

      const res: any = await postRequest(
        AcademyNavigatorApiEndpoints.UPLOAD_STORAGE_ORGANIZATION_FILE,
        formData,
        config
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchInvitationsSummary: async (
    context: any,
    payload: {
      registrationId: string;
      callback: Function;
    }
  ) => {
    const { registrationId, callback } = payload;

    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_INVITATIONS_SUMMARY}/${registrationId}/summary`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  saveInvitations: async (
    context: any,
    payload: {
      registrationId: string;
      body: any;
      isCheckboxChange: boolean;
      callback: Function;
    }
  ) => {
    const { registrationId, body, isCheckboxChange, callback } = payload;

    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.SAVE_INVITATIONS}/${registrationId}`,
        body
      );
      if (res.status === "success") {
        callback && callback(res.data);
        if (!isCheckboxChange) {
          context.dispatch(
            "snackbar/SHOW_SNACKBAR",
            { text: i18n.tc("snackbar_message.saved_successfully") },
            {
              root: true
            }
          );
        }
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchLaunchSummary: async (
    context: any,
    payload: {
      registrationId: string;
      callback: Function;
    }
  ) => {
    const { registrationId, callback } = payload;

    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_LAUNCH_SUMMARY}/${registrationId}/summary`
      );

      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  saveLaunchRegistrationDraft: async (
    context: any,
    payload: {
      registrationId: string;
      successCallback: Function;
      body: any;
    }
  ) => {
    const { registrationId, successCallback, body } = payload;

    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.SAVE_LAUNCH_REGISTRATION_DRAFT}/${registrationId}`,
        body
      );
      if (res.status === "success") {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  launchRegistration: async (
    context: any,
    payload: {
      registrationId: string;
      successCallback: Function;
      body: any;
    }
  ) => {
    const { registrationId, successCallback, body } = payload;

    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.LAUNCH_REGISTRATION}/${registrationId}/launch`,
        body
      );

      if (res.status === "success") {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchInvitedEmails: async (
    context: any,
    payload: {
      registrationId: string;
      registrationOptionId: string;
      callback: Function;
    }
  ) => {
    const { registrationId, registrationOptionId, callback } = payload;
    const query = prepareQueryParams({ registrationOptionId });
    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_INVITED_EMAILS}/${registrationId}/emails/invited${query}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchInvitedPlayersAndOrgUnits: async (
    context: any,
    payload: {
      registrationId: string;
      registrationOptionId: string;
      callback: Function;
    }
  ) => {
    const { registrationId, registrationOptionId, callback } = payload;
    const query = prepareQueryParams({ registrationOptionId });
    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_INVITED_PLAYERS_AND_ORG_UNITS}/${registrationId}/players-and-org-units/invited${query}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchInvitationPlayersAndOrgUnitsCandidates: async (
    context: any,
    payload: {
      registrationId: string;
      orgUnitId: string;
      filter: string;
      callback: Function;
    }
  ) => {
    const { registrationId, orgUnitId, filter, callback } = payload;
    const query = prepareQueryParams({ orgUnitId, filter });

    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_INVITATION_PLAYERS_AND_ORG_UNITS_CANDIDATES}/${registrationId}/players-and-org-units${query}`,
        { timeout: 10000 }
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchNotificationCandidates: async (
    context: any,
    payload: {
      registrationId: string;
      searchCriteria: {
        offset: string;
        pageSize: string;
        filter: string;
      };
      callback: Function;
    }
  ) => {
    const {
      registrationId,
      searchCriteria: { offset, pageSize, filter },
      callback
    } = payload;

    try {
      const query = prepareQueryParams({
        offset,
        pageSize,
        filter
      });
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_NOTIFICATION_CANDIDATES}/${registrationId}/candidates${query}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  saveNotificationCandidate: async (
    context: any,
    payload: {
      registrationId: string;
      body: any;
      callback: Function;
    }
  ) => {
    const { registrationId, body, callback } = payload;

    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.SAVE_NOTIFICATION_CANDIDATE}/${registrationId}`,
        body
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  updateNotificationCandidate: async (
    context: any,
    payload: {
      notificationId: string;
      body: any;
      callback: Function;
    }
  ) => {
    const { notificationId, body, callback } = payload;

    try {
      const res: any = await putRequest(
        `${TeamRegistrationApiEndpoints.UPDATE_NOTIFICATION_CANDIDATE}/${notificationId}`,
        body
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchNotifications: async (
    context: any,
    payload: {
      registrationId: string;

      callback: Function;
    }
  ) => {
    const { registrationId, callback } = payload;

    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_NOTIFICATIONS}/${registrationId}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  deleteNotification: async (
    context: any,
    payload: {
      registrationId: string;
      userId: string;
      callback: Function;
    }
  ) => {
    const { registrationId, userId, callback } = payload;

    try {
      const res: any = await deleteRequest(
        `${TeamRegistrationApiEndpoints.DELETE_NOTIFICATION}/${registrationId}/user/${userId}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchOrgUnitsOptions: async (
    context: any,
    payload: {
      registrationId: string;
      callback: Function;
    }
  ) => {
    const { registrationId, callback } = payload;

    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_ORG_UNITS_OPTIONS}/${registrationId}/org-units/options`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchRegistrationsSimpleList: async (
    context: any,
    payload: {
      callback: Function;
      queryParams: any;
    }
  ) => {
    try {
      const { queryParams } = payload;
      const query = prepareQueryParams({
        pageSize: 10,
        ...queryParams
      });
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_REGISTRATIONS_SIMPLE_LIST}${query}`
      );
      if (res.status === "success") {
        payload.callback && payload.callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  searchPlayersAndTeams: async (
    context: any,
    payload: {
      orgId: string;
      query: string;
      includeArchived: boolean;
      callback: Function;
    }
  ) => {
    const { orgId, query, includeArchived, callback } = payload;
    try {
      const queryParams = prepareQueryParams({
        query,
        includeArchived
      });
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.SEARCH_PLAYERS_AND_TEAMS}/${orgId}${queryParams}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  saveExistingAddOnItems: async (
    context: any,
    payload: {
      registrationId: string;
      callback: Function;
      addOnIds: [];
    }
  ) => {
    const { registrationId, addOnIds, callback } = payload;
    const body = { addOnIds };
    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.SAVE_EXISTING_ADD_ON_ITEMS}/${registrationId}/copy`,
        body
      );
      if (res.status === "success") {
        context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.added_successfully") },
          {
            root: true
          }
        );
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchTrackDetails: async (
    context: any,
    payload: {
      registrationId: string;
      trackType: string;
      page: number;
      filter: string;
      callback: Function;
    }
  ) => {
    const { registrationId, trackType, page, filter, callback } = payload;
    const query = prepareQueryParams({
      trackType,
      pageSize: 10,
      page: page - 1,
      filter: filter ? encodeURIComponent(filter) : null
    });

    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_TRACK_DETAILS}/${registrationId}${query}`
      );
      if (res.status === "success") {
        callback && callback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  exportCSV: async (
    context: any,
    payload: {
      registrationId: string;
    }
  ) => {
    try {
      await getRequest(
        `${TeamRegistrationApiEndpoints.EXPORT_CSV}/${payload.registrationId}/csv`,
        {
          [ApiRequestHeaderTypes.RESPONSE_TYPE]: ApiRequestHeaderValueTypes.BLOB
        }
      ).then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "registrations.csv");
        document.body.appendChild(link);
        link.click();
      });
    } catch (e) {
      console.log(e);
    }
  },

  fetchPaymentAccounts: async (
    context: any,
    payload: {
      registrationId: string;
      organizationId: string;
      successCallback: Function;
    }
  ) => {
    const { registrationId, organizationId, successCallback } = payload;
    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_PAYMENT_ACCOUNTS}/${organizationId}/${registrationId}`
      );
      if (res.status === "success") {
        context.commit("setAcceptPayments", {
          isAcceptPayments: true
        });
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  createPaymentAccount: async (
    context: any,
    payload: {
      organizationId: string;
      body: any;
      successCallback: Function;
    }
  ) => {
    const { organizationId, body, successCallback } = payload;
    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.CREATE_PAYMENT_ACCOUNT}/${organizationId}`,
        body
      );
      if (res.status === "success") {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  updateAcceptPayments: async (
    context: any,
    payload: {
      registrationId: string;
      acceptedPayments: boolean;
      successCallback: Function;
    }
  ) => {
    const { registrationId, acceptedPayments, successCallback } = payload;
    try {
      const res: any = await putRequest(
        `${TeamRegistrationApiEndpoints.UPDATE_ACCEPT_PAYMENTS}/${registrationId}/accept_payments`,
        { acceptedPayments }
      );
      if (res.status === "success") {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  createStripeAccount: async (
    context: any,
    payload: {
      paymentAccountId: string;
      successCallback: Function;
    }
  ) => {
    const { paymentAccountId, successCallback } = payload;
    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.CREATE_STRIPE_ACCOUNT}/${paymentAccountId}`
      );
      if (res.status === "success") {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  createPayPalAccount: async (
    context: any,
    payload: {
      email: string;
      paymentAccountId: string;
      fluidpayApiKey: string;
      successCallback: Function;
    }
  ) => {
    const { email, paymentAccountId, successCallback } = payload;
    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.CREATE_PAYPAL_ACCOUNT}/${paymentAccountId}`,
        { email }
      );
      if (res.status === "success") {
        successCallback && successCallback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  },

  createStripeOnboarding: async (
    context: any,
    payload: {
      stripeAccountId: string;
      returnPath: string;
      successCallback: Function;
    }
  ) => {
    const { stripeAccountId, returnPath, successCallback } = payload;
    try {
      const res: any = await postRequest(
        `${TeamRegistrationApiEndpoints.CREATE_STRIPE_ONBOARDING}/${stripeAccountId}`,
        { returnPath }
      );
      if (res.url) {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchMainPaymentAccount: async (
    context: any,
    payload: {
      registrationId: string;
      successCallback: Function;
    }
  ) => {
    const { registrationId, successCallback } = payload;
    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_MAIN_PAYMENT_ACCOUNT}/${registrationId}/main`
      );
      if (res.status === "success" && res.data !== null) {
        successCallback && successCallback(res.data);
        context.commit("setMainPaymentAccount", {
          mainPaymentAccount: res.data.paymentAccountId
        });
      }
    } catch (e) {
      console.log(e);
    }
  },

  updateMainPaymentAccount: async (
    context: any,
    payload: {
      paymentAccountId: string;
      registrationId: string;
      successCallback: Function;
    }
  ) => {
    const { paymentAccountId, registrationId, successCallback } = payload;
    if (!paymentAccountId) {
      context.commit("setMainPaymentAccount", {
        mainPaymentAccount: "No Online Payment"
      });
      successCallback && successCallback(paymentAccountId);
    } else {
      try {
        const res: any = await putRequest(
          `${TeamRegistrationApiEndpoints.UPDATE_MAIN_PAYMENT_ACCOUNT}/${registrationId}/main/${paymentAccountId}`
        );
        if (res.status === "success") {
          context.commit("setMainPaymentAccount", {
            mainPaymentAccount: res.data
          });
          successCallback && successCallback(res.data);
        }
      } catch (e) {
        console.log(e);
      }
    }
  },

  fetchPlayerRegistrationAnswers: async (
    context: any,
    payload: {
      registrationPlayerId: string;
      successCallback: Function;
    }
  ) => {
    const { registrationPlayerId, successCallback } = payload;
    try {
      const res: any = await getRequest(
        `${TeamRegistrationApiEndpoints.FETCH_REGISTRATION_PLAYER_ANSWERS}/${registrationPlayerId}/info`
      );
      if (res.status === "success") {
        successCallback && successCallback(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
